import { Component, OnDestroy, OnInit } from '@angular/core';
import { CertificationInterface } from 'src/app/model/certifications/certification-interface';
import { CertificationService } from 'src/app/services/dataServices/certification-service/certificationService';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';
import { AttributeService } from 'src/app/services/dataServices/attribute-service/attributeService';
import { Router } from '@angular/router';
import { DialogService } from 'src/app/services/dialog-service/dialog-service.service';
import { AttributeValidationServiceService } from 'src/app/services/dataServices/dataValidation/attribute-validation-service/attribute-validation-service.service';
import { CertificationVariantInterface } from 'src/app/model/certifications/certification-variant-interface';
import { CertificationVariantVersionInterface } from 'src/app/model/certifications/certification-variant-version-interface';
import { UtilService } from 'src/app/services/util-service/utilService';
import { UserService } from 'src/app/services/user-service/userservice';
import { firstValueFrom } from 'rxjs';
import { CertificationTypes } from 'src/app/model/enums/certificationTypes';
import { IssuerCertificationCatalogueApiService } from 'src/app/construct-x/data-space/services/issuer-certification-catalogue-api.service';
import { IssuerCertificationMapper } from 'src/app/construct-x/data-space/services/issuer-certification.mapper';

@Component({
    selector: 'app-certification-screen',
    templateUrl: './certification-screen.component.html',
    styleUrls: ['./certification-screen.component.css'],
    standalone: false
})
export class CertificationScreenComponent implements OnInit, OnDestroy {
    public certification: CertificationInterface;
    isFormValid: boolean = false;
    isDescriptionValid: boolean = true;

    constructor(
        public organisationService: OrganisationService,
        public certificationService: CertificationService,
        public attributeService: AttributeService,
        public router: Router,
        private dialogService: DialogService,
        public attributeValidationService: AttributeValidationServiceService,
        private userService: UserService,
        private issuerCatalogueApi: IssuerCertificationCatalogueApiService,
        private issuerCertificationMapper: IssuerCertificationMapper
    ) {
        //Reroutes to marketplace if the user is not a member of the organisation
        document.documentElement.style.setProperty('--scrollStatus', 'auto');
        if (
            !this.userService.currentMemberships.find(
                (orga) =>
                    orga.organisationId ===
                    this.organisationService.activeItem.organisationId
            )
        ) {
            this.router.navigateByUrl('/marktplatz');
        }
    }

    ngOnInit(): void {
        if (this.organisationService && this.organisationService.activeItem) {
            this.certification = {
                company: this.organisationService.activeItem.organisationId,
                category: '1',
                certificationType: 0,
                description: '',
                galleryFileIds: [],
                documentFileIds: [],
                listingPkgName: null,
                certificationVariants: [
                    {
                        description: '',
                        certificationVariantVersions: [{}]
                    }
                ]
            };
        }
    }

    ngOnDestroy(): void {
        document.documentElement.style.setProperty('--scrollStatus', 'hidden');
    }

    cancelCreateCertification(): void {
        if (this.certificationService.activeItem) {
            this.router.navigate([
                '/organisations/' +
                this.organisationService.activeItem.organisationId +
                '/certifications/' +
                this.certificationService.activeItem.id
            ]);
        } else {
            this.router.navigate([
                '/organisations/' +
                this.organisationService.activeItem.organisationId +
                '/certifications'
            ]);
        }
    }

    createCertification(): void {
        if (
            !UtilService.areStringsValid(
                this.certification.name,
                this.certification.description
            )
        ) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Der Name oder die Beschreibung sind zu lang.'
            );
            return;
        }

        if (!this.isInputValid()) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte stellen Sie sicher, dass alle Merkmale validiert sind' +
                ' oder entfernen Sie alle Produkttypzugehörigkeiten, von denen nicht alle Merkmale validiert sind.'
            );
            return;
        }

        if (this.isIssuerCertification()) {
            this.createIssuerCertification();
            return;
        }

        if (this.certification.fileId == null) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte wählen Sie ein Logo für die Zertifizierung.'
            );
            return;
        }

        // first variant
        let certificationVariant: CertificationVariantInterface =
            this.certification.certificationVariants[0];
        // first version
        let certificationVariantVersion: CertificationVariantVersionInterface =
            certificationVariant.certificationVariantVersions[0];

        if (!UtilService.areStringsValid(certificationVariant.name, null)) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte stellen sie sicher, dass der Name der Variante nicht zu lang ist.'
            );
            return;
        }
        if (
            !UtilService.areStringsValid(certificationVariantVersion.name, null)
        ) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte stellen sie sicher, dass der Name der Version nicht zu lang ist.'
            );
            return;
        }
        // validate
        this.certification.certificationVariants.forEach(
            (variant: CertificationVariantInterface) =>
                variant.certificationVariantVersions.forEach(
                    (version: CertificationVariantVersionInterface) => {
                        version.shape = this.certification.shape;
                        version.target = "0";
                    }
                )
        );
        if (this.certification.description == null) {
            this.certification.description = '';
        }
        if (certificationVariant == null) {
            certificationVariant.description = '';
        }
        if (certificationVariantVersion.description == null) {
            certificationVariantVersion.description = '';
        }
        certificationVariantVersion.attributeValidationValues =
            this.attributeValidationService.getAttributeValidationValues();

        //TODO if userOrganisationRole is User and product has active Version, prevent the creation and tell the user
        //TODO Manual setting of certificationType,
        this.certificationService.createCertification(this.certification).then(
            async (c: CertificationInterface) => {
                this.certificationService.loadCertificationsForOrganisation(
                    this.organisationService.activeOrganisation
                );
                this.router.navigateByUrl(
                    '/organisations/' +
                    this.organisationService.activeItem.organisationId +
                    '/certifications/' +
                    c.id
                );
            },
            (e) => {
                this.dialogService.openDialog(
                    'Zertifizierung nicht erstellt',
                    e.error
                );
            }
        );
    }

    private async createIssuerCertification(): Promise<void> {
        if (!this.certification.shape?.trim()) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte definieren Sie ein gültiges JSON-Schema für die Zertifizierung.'
            );
            return;
        }

        try {
            const created = await firstValueFrom(
                this.issuerCatalogueApi.createCredentialDefinitionWithAttestation(
                    'con-x-issuer',
                    {
                        holders: [],
                        definition: {
                            label: this.certification.name.trim(),
                            description:
                                this.certification.description?.trim() ||
                                undefined,
                            jsonSchema:
                                this.issuerCertificationMapper.toJsonSchema(
                                    this.certification.shape,
                                    this.certification.name.trim(),
                                    this.certification.description
                                ),
                            sourceReference:
                                this.certification.url?.trim() || undefined
                        }
                    },
                    this.organisationService.activeItem.organisationId
                )
            );
            await this.certificationService.loadCertificationsForOrganisation(
                this.organisationService.activeOrganisation
            );
            const definitionId =
                created.credentialDefinition.issuerCredentialDefinitionId;
            const projected = this.certificationService.collection.find(
                (certification) =>
                    certification.issuerCredentialDefinitionId === definitionId
            );
            this.router.navigateByUrl(
                `/organisations/${this.organisationService.activeItem.organisationId}/certifications${projected ? `/${projected.id}` : ''}`
            );
        } catch (error: any) {
            this.dialogService.openDialog(
                'Zertifizierung nicht erstellt',
                error?.error?.message ??
                    error?.error?.error ??
                    'Die Zertifizierung konnte im Issuer Service nicht angelegt werden.'
            );
        }
    }

    private isIssuerCertification(): boolean {
        return (
            this.certification.certificationType === 3 ||
            this.certification.certificationType === '3' ||
            this.certification.certificationType ===
                CertificationTypes.ONTOLOGY_CERT
        );
    }

    private isInputValid(): boolean {
        return this.attributeValidationService.checkAllEntityAttributeValuesValidated();
    }

    public onValidityChanged(valid: boolean): void {
        this.isFormValid = valid;
    }

    public onDescriptionValidityChange(descriptionValid: boolean): void {
        this.isDescriptionValid = descriptionValid;
    }
}
